import { ClickAwayListener } from "@mui/base";
import classnames from "classnames";
import type { FunctionComponent } from "react";
import { useState } from "react";
import { graphql, useFragment } from "react-relay";

import { config } from "shared/data";
import { formatInTimeZone, notEmpty } from "shared/lib/utils";

import { ArticleImageGallery } from "scmp-app/components/article/article-image-gallery";
import type { DefaultComponentsProvider } from "scmp-app/components/article/article-render";
import { useLiveArticleScrollContext } from "scmp-app/components/article/article-render/live-article/contexts";
import { getContentItemClassName } from "scmp-app/components/article/article-render/live-article/helpers";
import { ContentSchemaRender } from "scmp-app/components/schema-render/content";
import type { itemLiveArticleContentArticle$key } from "scmp-app/queries/__generated__/itemLiveArticleContentArticle.graphql";
import type { itemLiveArticleContentLiveContent$key } from "scmp-app/queries/__generated__/itemLiveArticleContentLiveContent.graphql";

import {
  ActionBox,
  Authors,
  Container,
  Content,
  Header,
  ShareBar,
  ShareButton,
  ShareLabel,
  StyledBookmarkLogo,
  StyledContentShareWidget,
  StyledShareButtonIcon,
  StyledTooltip,
  TheEnd,
  Time,
  Title,
} from "./styles";

type Props = {
  articleReference: itemLiveArticleContentArticle$key;
  className?: string;
  isTheEnd?: boolean;
  liveContentReference: itemLiveArticleContentLiveContent$key;
  provider: DefaultComponentsProvider;
};

export const LiveArticleContentItem: FunctionComponent<Props> = ({
  articleReference,
  className,
  isTheEnd = false,
  liveContentReference,
  provider,
}) => {
  const { captureContentItemElement } = useLiveArticleScrollContext();
  const [isShowShareBar, setIsShowShareBar] = useState(false);

  const article = useFragment(
    graphql`
      fragment itemLiveArticleContentArticle on Article {
        entityId
        urlAlias
        ...articleImageGalleryContent
        ...contentSchemaRenderContent
        ...contentShareWidgetContent
      }
    `,
    articleReference,
  );

  const liveContent = useFragment(
    graphql`
      fragment itemLiveArticleContentLiveContent on LiveContent {
        customTime
        entityId
        entityUuid
        title
        authors {
          name
        }
        summary {
          text
        }
        body {
          json
        }
        ...contentShareWidgetLiveContent
      }
    `,
    liveContentReference,
  );

  const renderTime = () => {
    if (!notEmpty(liveContent?.customTime)) return null;
    return <Time>{formatInTimeZone(liveContent.customTime, "h:mma")}</Time>;
  };

  const renderTheEnd = () => {
    if (!isTheEnd) return null;
    return <TheEnd>The End</TheEnd>;
  };

  const renderBookmarkIcon = () => {
    if (!notEmpty(liveContent?.summary?.text)) return null;
    return <StyledBookmarkLogo />;
  };

  const renderTitle = () => {
    if (!notEmpty(liveContent?.title)) return null;
    return <Title>{liveContent.title}</Title>;
  };

  const renderAuthors = () => {
    const prefix = "From ";
    const authors = liveContent?.authors ?? [];
    if (authors.length === 0) return null;

    return (
      <Authors>
        {prefix +
          authors
            .map(author => author?.name ?? "")
            .join(", ")
            .replace(/(, )([^,]+)$/, ` and $2`)}
      </Authors>
    );
  };

  return (
    <Container
      className={classnames(className, getContentItemClassName(liveContent.entityUuid))}
      ref={captureContentItemElement}
    >
      <Header>
        {renderTime()}
        {renderTheEnd()}
        {renderBookmarkIcon()}
      </Header>
      {renderTitle()}
      {renderAuthors()}
      <Content>
        <ArticleImageGallery reference={article}>
          <ContentSchemaRender reference={article} schema={liveContent.body?.json} />
        </ArticleImageGallery>
      </Content>
      <ActionBox>
        {provider.commentTrigger({
          onClick: () => {
            if (!article.entityId || !article?.urlAlias || !liveContent.entityId) return;

            const url = new URL(article.urlAlias, config.general.siteUrl);
            url.searchParams.set("utm_source", "Comment");
            url.searchParams.set("utm_medium", "share_widget");
            url.searchParams.set("utm_campaign", article.entityId);
            url.hash = `live-${liveContent.entityId}`;
          },
          variant: "live-article-item",
        })}
        <StyledTooltip
          open={isShowShareBar}
          placement="bottom-start"
          PopperProps={{
            disablePortal: true,
          }}
          title={
            <ClickAwayListener
              onClickAway={() => {
                setIsShowShareBar(false);
              }}
            >
              <ShareBar>
                <ShareLabel>Share</ShareLabel>
                <StyledContentShareWidget
                  extraProps={{
                    copyLink: {
                      tooltipPlacement: "top",
                      variant: "tooltip",
                    },
                  }}
                  liveContentReference={liveContent}
                  position="live_snippet"
                  reference={article}
                  types={["facebook", "twitter", "copy-link", "email"]}
                />
              </ShareBar>
            </ClickAwayListener>
          }
        >
          <ShareButton
            onClick={() => {
              setIsShowShareBar(!isShowShareBar);
            }}
          >
            <StyledShareButtonIcon />
          </ShareButton>
        </StyledTooltip>
      </ActionBox>
    </Container>
  );
};

LiveArticleContentItem.displayName = "LiveArticleContentItem";
